import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp, PieChart, Plus, Loader2, Sparkles, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CostEstimation = ({ project }) => {
  const [estimates, setEstimates] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [costItems, setCostItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateEstimate, setShowCreateEstimate] = useState(false);
  const [showCreateItem, setShowCreateItem] = useState(false);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    fetchEstimates();
  }, [project.id]);

  useEffect(() => {
    if (selectedEstimate) {
      fetchCostItems(selectedEstimate.id);
    }
  }, [selectedEstimate]);

  const fetchEstimates = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/cost-estimates`);
      setEstimates(response.data);
      if (response.data.length > 0 && !selectedEstimate) {
        setSelectedEstimate(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching estimates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCostItems = async (estimateId) => {
    try {
      const response = await axios.get(`${API}/cost-estimates/${estimateId}/items`);
      setCostItems(response.data);
    } catch (error) {
      console.error('Error fetching cost items:', error);
    }
  };

  const createEstimate = async (estimateData) => {
    try {
      const newEstimate = {
        ...estimateData,
        project_id: project.id,
      };
      const response = await axios.post(`${API}/cost-estimates`, newEstimate);
      await fetchEstimates();
      setSelectedEstimate(response.data);
      setShowCreateEstimate(false);
    } catch (error) {
      console.error('Error creating estimate:', error);
    }
  };

  const calculateWithAI = async () => {
    if (!selectedEstimate) return;
    setCalculating(true);
    try {
      await axios.post(`${API}/cost-estimates/${selectedEstimate.id}/calculate-ai`);
      await fetchEstimates();
      await fetchCostItems(selectedEstimate.id);
    } catch (error) {
      console.error('Error calculating costs:', error);
    } finally {
      setCalculating(false);
    }
  };

  const createCostItem = async (itemData) => {
    try {
      const newItem = {
        ...itemData,
        cost_estimate_id: selectedEstimate.id,
        project_id: project.id,
        total_cost: itemData.quantity * itemData.unit_cost,
      };
      await axios.post(`${API}/cost-items`, newItem);
      await fetchEstimates();
      await fetchCostItems(selectedEstimate.id);
      setShowCreateItem(false);
    } catch (error) {
      console.error('Error creating cost item:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'resource': return 'bg-blue-100 text-blue-700';
      case 'licensing': return 'bg-purple-100 text-purple-700';
      case 'infrastructure': return 'bg-green-100 text-green-700';
      case 'training': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const costBreakdown = selectedEstimate ? [
    { label: 'Resource Costs', value: selectedEstimate.resource_cost, color: 'bg-blue-500' },
    { label: 'Licensing', value: selectedEstimate.licensing_cost, color: 'bg-purple-500' },
    { label: 'Infrastructure', value: selectedEstimate.infrastructure_cost, color: 'bg-green-500' },
    { label: 'Training', value: selectedEstimate.training_cost, color: 'bg-orange-500' },
    { label: 'Contingency', value: selectedEstimate.contingency_cost, color: 'bg-gray-500' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Cost Estimation Engine</h2>
            <p className="text-gray-600">Comprehensive TCO calculator for your SAP migration</p>
          </div>
          <button
            onClick={() => setShowCreateEstimate(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Estimate</span>
          </button>
        </div>
      </div>

      {estimates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-100">
          <DollarSign className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Cost Estimates Yet</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">Create your first cost estimate to start planning your migration budget</p>
          <button
            onClick={() => setShowCreateEstimate(true)}
            className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create Estimate</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Estimate Selector */}
          <div className="lg:col-span-1 space-y-4">
            {estimates.map((estimate) => (
              <div
                key={estimate.id}
                onClick={() => setSelectedEstimate(estimate)}
                className={`bg-white rounded-xl p-4 cursor-pointer border-2 transition-all ${
                  selectedEstimate?.id === estimate.id
                    ? 'border-blue-500 shadow-lg'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <h3 className="font-semibold text-gray-900 mb-2">{estimate.name}</h3>
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="text-2xl font-bold text-blue-600">
                    {formatCurrency(estimate.total_cost_usd)}
                  </span>
                  <span className="text-xs text-gray-500">{estimate.currency}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {estimate.total_effort_hours.toLocaleString()} hours
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    estimate.confidence_level === 'high' ? 'bg-green-100 text-green-700' :
                    estimate.confidence_level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {estimate.confidence_level} confidence
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Estimate Details */}
          <div className="lg:col-span-2 space-y-6">
            {selectedEstimate && (
              <>
                {/* Total Cost Overview */}
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-lg p-8 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-blue-100 text-sm mb-2">Total Cost of Ownership (TCO)</p>
                      <h3 className="text-5xl font-bold">{formatCurrency(selectedEstimate.total_cost_usd)}</h3>
                    </div>
                    <DollarSign className="w-16 h-16 opacity-30" />
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div>
                      <p className="text-blue-100 text-xs mb-1">Effort Hours</p>
                      <p className="text-2xl font-semibold">{selectedEstimate.total_effort_hours.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-blue-100 text-xs mb-1">Confidence</p>
                      <p className="text-2xl font-semibold capitalize">{selectedEstimate.confidence_level}</p>
                    </div>
                    <div>
                      <p className="text-blue-100 text-xs mb-1">Currency</p>
                      <p className="text-2xl font-semibold">{selectedEstimate.currency}</p>
                    </div>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Cost Breakdown</h3>
                  <div className="space-y-4">
                    {costBreakdown.map((item, idx) => (
                      <div key={idx} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-700">{item.label}</span>
                          <span className="font-bold text-gray-900">{formatCurrency(item.value)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className={`${item.color} h-3 rounded-full transition-all`}
                            style={{
                              width: `${(item.value / selectedEstimate.total_cost_usd) * 100}%`
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cost Items */}
                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Cost Items</h3>
                    <div className="flex space-x-3">
                      <button
                        onClick={calculateWithAI}
                        disabled={calculating}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50"
                      >
                        {calculating ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Calculating...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            <span className="text-sm">AI Calculate</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowCreateItem(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-sm">Add Item</span>
                      </button>
                    </div>
                  </div>

                  {costItems.length === 0 ? (
                    <div className="text-center py-12">
                      <PieChart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600">No cost items yet. Use AI to calculate or add manually.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Quantity</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Unit Cost</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {costItems.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-gray-900">{item.item_name}</p>
                                  <p className="text-xs text-gray-500">{item.description}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${getCategoryColor(item.category)}`}>
                                  {item.category}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-gray-900">{item.quantity}</td>
                              <td className="px-4 py-3 text-right font-medium text-gray-900">
                                {formatCurrency(item.unit_cost)}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-gray-900">
                                {formatCurrency(item.total_cost)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateEstimate && (
        <CreateEstimateModal
          onClose={() => setShowCreateEstimate(false)}
          onCreate={createEstimate}
        />
      )}

      {showCreateItem && selectedEstimate && (
        <CreateCostItemModal
          onClose={() => setShowCreateItem(false)}
          onCreate={createCostItem}
        />
      )}
    </div>
  );
};

// Create Estimate Modal
const CreateEstimateModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    confidence_level: 'medium',
    currency: 'USD',
    total_cost_usd: 0,
    total_effort_hours: 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Create Cost Estimate</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Estimate Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Q1 2024 Migration Budget"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confidence Level</label>
              <select
                value={formData.confidence_level}
                onChange={(e) => setFormData({ ...formData, confidence_level: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Create Estimate
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Cost Item Modal
const CreateCostItemModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    category: 'resource',
    item_name: '',
    description: '',
    quantity: 1,
    unit_cost: 0,
    cost_type: 'one_time',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Add Cost Item</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Item Name *</label>
            <input
              type="text"
              required
              value={formData.item_name}
              onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., SAP Consultant"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief description"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="resource">Resource</option>
                <option value="licensing">Licensing</option>
                <option value="infrastructure">Infrastructure</option>
                <option value="training">Training</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Cost Type</label>
              <select
                value={formData.cost_type}
                onChange={(e) => setFormData({ ...formData, cost_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="one_time">One-Time</option>
                <option value="recurring">Recurring</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Unit Cost (USD) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.unit_cost}
                onChange={(e) => setFormData({ ...formData, unit_cost: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Total Cost:</span>{' '}
              <span className="text-lg font-bold text-blue-600">
                ${(formData.quantity * formData.unit_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </p>
          </div>
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Add Cost Item
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CostEstimation;